import { useEffect, useRef } from 'react';
import { ampli } from '~/domain/plugins/analytics';
import { usePluginsReady } from '~/domain/plugins/plugins-ready';
import type { AvailabilityMode } from '~/domain/availability/availability-mode';
import type { BookingMode } from '~/domain/availability/booking-mode';
import {
  isBookableStatus,
  type AvailabilityStatus,
} from '~/domain/availability/types';
import { token } from '~/styled-system/tokens';

/**
 * Amplitude events for the experience page's booking widget (US-13062,
 * US-13063). None of them are `Checkout *` events, so the checkout enrichment
 * never stamps them and every payload carries `experience_id` itself.
 */

export type WidgetSurface = 'bar' | 'panel';
export type WidgetMode = BookingMode | 'open';

/**
 * The media query Panda emits for `hideFrom: 'tablet'` / `hideBelow: 'tablet'`,
 * which is what actually decides whether the bar or the panel shows. Panda
 * writes breakpoints in rem, and a rem media query follows the browser's
 * default font size rather than the root element's, so a px query built from
 * the same token would disagree for visitors who changed that default.
 */
export const TABLET_UP_QUERY = `screen and (min-width: ${pxToRem(
  token('breakpoints.tablet')
)})`;

function pxToRem(px: string): string {
  return `${parseFloat(px) / 16}rem`;
}

export function surfaceFor(matchesTabletUp: boolean): WidgetSurface {
  return matchesTabletUp ? 'panel' : 'bar';
}

/** Client-only: both surfaces are in the DOM, so only the viewport can say
 *  which one the visitor sees. */
export function currentSurface(): WidgetSurface {
  return surfaceFor(window.matchMedia(TABLET_UP_QUERY).matches);
}

/** Open-timeslot experiences report `calendar` as their booking mode, but
 *  their panel is a different widget altogether, so it gets a mode of its own. */
export function widgetModeFor(
  availabilityMode: AvailabilityMode,
  bookingWidgetMode: BookingMode
): WidgetMode {
  return availabilityMode === 'openTimeslots' ? 'open' : bookingWidgetMode;
}

/** Same values `Experience Booking Panel Book Now Clicked` has always sent. */
export function availabilityModeValue(
  availabilityMode: AvailabilityMode
): 'scheduled' | 'open' {
  return availabilityMode === 'openTimeslots' ? 'open' : 'scheduled';
}

/**
 * Days from now until `date` (YYYY-MM-DD, or an ISO timestamp whose date part
 * is used). Computed exactly like the checkout's `Checkout Event list Event
 * Selected` so the two events' day counts can be compared.
 */
export function daysUntil(date: string, now: number): number {
  // `|| 0` folds the -0 a same-day date rounds to.
  return (
    Math.round((new Date(date.slice(0, 10)).getTime() - now) / 86_400_000) || 0
  );
}

type WidgetEvent = { startTime: string; status: AvailabilityStatus };

export type WidgetViewInput = {
  experienceId: string;
  availabilityMode: AvailabilityMode;
  bookingWidgetMode: BookingMode;
  events: readonly WidgetEvent[];
  canProceedToCheckout: boolean;
};

export function buildWidgetViewedPayload(
  input: WidgetViewInput & { surface: WidgetSurface; now: number }
) {
  const isOpen = input.availabilityMode === 'openTimeslots';
  // Events arrive in start order, so the first bookable one is the earliest.
  const firstBookable = isOpen
    ? undefined
    : input.events.find((e) => isBookableStatus(e.status));
  return {
    experience_id: input.experienceId,
    surface: input.surface,
    availability_mode: availabilityModeValue(input.availabilityMode),
    widget_mode: widgetModeFor(input.availabilityMode, input.bookingWidgetMode),
    can_proceed_to_checkout: input.canProceedToCheckout,
    ...(isOpen ? {} : { event_count: input.events.length }),
    ...(firstBookable
      ? {
          days_until_first_event: daysUntil(firstBookable.startTime, input.now),
        }
      : {}),
  };
}

/**
 * Keyed on the experience rather than a plain "fired" flag: the page
 * component stays mounted across a client-side navigation to another
 * experience, which is a new page view, while a revalidation of the same
 * experience (filters, opening the booking dialog) is not.
 */
export function shouldTrackWidgetView({
  pluginsReady,
  lastTrackedExperienceId,
  experienceId,
}: {
  pluginsReady: boolean;
  lastTrackedExperienceId: string | null;
  experienceId: string;
}): boolean {
  return pluginsReady && lastTrackedExperienceId !== experienceId;
}

export function useTrackBookingWidgetViewed({
  experienceId,
  availabilityMode,
  bookingWidgetMode,
  events,
  canProceedToCheckout,
}: WidgetViewInput) {
  const pluginsReady = usePluginsReady();
  const lastTracked = useRef<string | null>(null);

  useEffect(() => {
    if (
      !shouldTrackWidgetView({
        pluginsReady,
        lastTrackedExperienceId: lastTracked.current,
        experienceId,
      })
    ) {
      return;
    }
    lastTracked.current = experienceId;
    ampli.experienceBookingWidgetViewed(
      buildWidgetViewedPayload({
        experienceId,
        availabilityMode,
        bookingWidgetMode,
        events,
        canProceedToCheckout,
        surface: currentSurface(),
        now: Date.now(),
      })
    );
  }, [
    pluginsReady,
    experienceId,
    availabilityMode,
    bookingWidgetMode,
    events,
    canProceedToCheckout,
  ]);
}

export function trackBarClicked({
  experienceId,
  widgetMode,
  isEmpty,
}: {
  experienceId: string;
  widgetMode: WidgetMode;
  isEmpty: boolean;
}): void {
  ampli.experienceBookingBarClicked({
    experience_id: experienceId,
    widget_mode: widgetMode,
    is_empty: isEmpty,
  });
}

export function trackPanelEventSelected({
  experienceId,
  widgetMode,
  positionInList,
  startTime,
  now = Date.now(),
}: {
  experienceId: string;
  widgetMode: WidgetMode;
  positionInList: number;
  startTime: string;
  now?: number;
}): void {
  ampli.experienceBookingPanelEventSelected({
    experience_id: experienceId,
    widget_mode: widgetMode,
    position_in_list: positionInList,
    days_until_event: daysUntil(startTime, now),
  });
}

export function trackPanelDateSelected({
  experienceId,
  widgetMode,
  date,
  now = Date.now(),
}: {
  experienceId: string;
  widgetMode: WidgetMode;
  date: string;
  now?: number;
}): void {
  ampli.experienceBookingPanelDateSelected({
    experience_id: experienceId,
    widget_mode: widgetMode,
    days_until_date: daysUntil(date, now),
  });
}

export function trackPanelWaitlistOpened({
  experienceId,
  widgetMode,
  surface = currentSurface(),
}: {
  experienceId: string;
  widgetMode: WidgetMode;
  surface?: WidgetSurface;
}): void {
  ampli.experienceBookingPanelWaitlistOpened({
    experience_id: experienceId,
    widget_mode: widgetMode,
    surface,
  });
}
